import { type PropsWithChildren } from "react";

import { ExternalLinkIcon } from "#icons";
import { cn } from "#utils";

// Type-only import; the value dependency runs the other way (NavItemList
// renders SidebarLink), so no runtime cycle is introduced.
import { type SidebarVariant } from "./NavItemList";

function matchesPath(pathname: string, to: string) {
  const normalize = (path: string) => path.replace(/\/+$/, "") || "/";

  const normalizedPathname = normalize(pathname);
  const normalizedTo = normalize(to);

  // Ensure exact match or valid subpath with trailing slash
  return normalizedPathname === normalizedTo || normalizedPathname.startsWith(`${normalizedTo}/`);
}

function getIsActive(pathname: string, target: string, activePaths?: string[]) {
  return (
    matchesPath(pathname, target) ||
    (activePaths?.some((path) => matchesPath(pathname, path)) ?? false)
  );
}

interface Props extends PropsWithChildren {
  target: string;
  pathname: string;
  navigate: (to: string) => void;
  isExternalLink?: boolean;
  className?: string;
  isChild?: boolean;
  /** Indent the link to nest it visually beneath a collapsible section heading. */
  indented?: boolean;
  activePaths?: string[];
  /** Visual style of the link, matching the Sidebar variant it renders in. */
  variant?: SidebarVariant;
}

export function SidebarLink({
  children,
  isExternalLink,
  target,
  navigate,
  pathname,
  className,
  isChild,
  indented,
  activePaths,
  variant = "default",
}: Props) {
  const isActive = getIsActive(pathname, target, activePaths);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    // Prevent default navigation if left-click is used
    if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      navigate(target);
    }
  };

  if (isExternalLink) {
    return (
      <a
        className={cn("flex items-center justify-between", className)}
        href={target}
        rel="noreferrer"
        target="_blank"
      >
        {children}
        <ExternalLinkIcon />
      </a>
    );
  }

  if (variant === "grouped") {
    // Flat row per the grouped design: no indentation or accent border; the
    // active row is a full-width brand wash with the label darkened, and the
    // icon inherits the label color via currentColor.
    return (
      <a
        className={cn(
          "flex w-full cursor-pointer items-center py-xxs pl-xl pr-[20px] text-label-small font-medium transition-colors duration-300",
          isActive
            ? "bg-overlay-brand text-text-primary"
            : "text-text-secondary hover:text-text-primary",
          className,
        )}
        href={target}
        onClick={handleClick}
      >
        {children}
      </a>
    );
  }

  return (
    <a
      className={cn(
        "flex w-full cursor-pointer items-center border-r-2 py-xxs pr-[20px] transition-colors duration-300",
        indented ? "pl-4xl" : "pl-xl",
        isActive ? "border-outline-brand bg-overlay-brand" : "border-transparent",
        className,
      )}
      href={target}
      onClick={handleClick}
    >
      <div
        className={cn(
          "transition-colors duration-300",
          isChild ? "text-label-small font-normal text-text-secondary" : "text-title-small",
          isActive ? "font-semibold text-text-brand" : "",
        )}
      >
        {children}
      </div>
    </a>
  );
}
